import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Warehouse as WarehouseIcon,
  VerifiedUser as UserIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';

// Import the role manager component
import UserRoleManager from '../components/UserRoleManager';
import CategoryManager from '../components/CategoryManager';
import ErrorBoundary from '../components/ErrorBoundary';
// import SystemSettings from '../components/SystemSettings';
// import AdvancedSettings from '../components/AdvancedSettings';
import { useAuth } from '../contexts/AuthContext';
import FeatureGate from '../components/FeatureGate';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

/**
 * TabPanel component for displaying tab content
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Admin Page Component
 * 
 * This page provides an interface for administrators to manage
 * various aspects of the application.
 */
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  
  // Check if user has admin role
  const isAdmin = user?.roles?.includes('admin');
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    console.log('AdminPage: Tab changed to:', newValue);
    setActiveTab(newValue);
  };

  // Define available tabs based on feature flags
  const availableTabs = [
    { id: 'users', icon: <UserIcon />, label: 'User Management', component: <UserRoleManager /> },
    ...(isFeatureEnabled('EQUIPMENT_CATEGORIES') ? [{
      id: 'categories', 
      icon: <CategoryIcon />, 
      label: 'Categories', 
      component: <CategoryManager />
    }] : [])
  ];
  
  // Debug available tabs
  console.log('AdminPage: Available tabs:', availableTabs.map(tab => ({ id: tab.id, label: tab.label })));
  console.log('AdminPage: Current active tab:', activeTab);
  
  // If user is not an admin, show access denied message
  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            You do not have permission to access the admin panel. 
            Please contact an administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>
      
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: '#666666',
              fontWeight: 500,
              minHeight: 72,
              '&.Mui-selected': {
                color: '#2C2C2C',
                fontWeight: 600,
              },
              '&:hover': {
                color: '#4A4A4A',
              }
            },
            '& .MuiTabs-indicator': {
              height: 4,
              backgroundColor: '#2C2C2C',
            }
          }}
        >
          {availableTabs.map((tab, index) => (
            <Tab key={tab.id} icon={tab.icon} label={tab.label} />
          ))}
        </Tabs>
      </Paper>
      
      {availableTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={activeTab} index={index}>
          {tab.component}
        </TabPanel>
      ))}
      
      {/* <TabPanel value={activeTab} index={4}>
        <SystemSettings />
      </TabPanel>
      
      <TabPanel value={activeTab} index={5}>
        <AdvancedSettings />
      </TabPanel> */}
    </Box>
  );
};

export default AdminPage;